import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, ChevronLeft, ChevronRight, Trash2, Eye } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { toast } from "sonner";

const BG_PALETTE = [
  "linear-gradient(135deg,#618DB8,#2A4365)",
  "linear-gradient(135deg,#FF7A5C,#a8421f)",
  "linear-gradient(135deg,#10b981,#047857)",
  "linear-gradient(135deg,#8b5cf6,#5b21b6)",
  "linear-gradient(135deg,#FFB400,#b45309)",
  "linear-gradient(135deg,#0FACD7,#0e7490)",
];

function timeLeft(expIso) {
  const ms = new Date(expIso).getTime() - Date.now();
  if (ms <= 0) return "expirée";
  const h = Math.floor(ms / 3600000);
  if (h >= 1) return `${h} h restantes`;
  const m = Math.floor(ms / 60000);
  return `${m} min restantes`;
}

function StoryCircle({ bundle, onClick, isCreate = false, user }) {
  if (isCreate) {
    return (
      <button onClick={onClick} className="flex flex-col items-center gap-1.5 w-[72px] shrink-0 group" data-testid="story-create-btn">
        <div className="relative w-[68px] h-[68px] rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center group-hover:border-[#FF7A5C] transition">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF7A5C] to-[#e0593c] text-white flex items-center justify-center">
            <Plus className="h-5 w-5" />
          </div>
        </div>
        <span className="text-[11px] font-semibold text-slate-700 truncate w-full text-center">Ma story</span>
      </button>
    );
  }
  const unseen = bundle.has_unseen;
  const ring = unseen
    ? "bg-gradient-to-tr from-[#FF7A5C] via-[#FFB400] to-[#618DB8]"
    : "bg-slate-200";
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1.5 w-[72px] shrink-0" data-testid={`story-bundle-${bundle.author.user_id}`}>
      <div className={`relative w-[68px] h-[68px] rounded-full p-[2px] ${ring}`}>
        <div className="w-full h-full rounded-full bg-white p-[2px]">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-extrabold text-base">
            {(bundle.author.name || "?").replace(/^Dr\s+/, "").charAt(0)}
          </div>
        </div>
        {bundle.is_mine && (
          <span className="absolute -bottom-0.5 -right-0.5 bg-[#FF7A5C] rounded-full p-0.5 border-2 border-white">
            <Plus className="h-2.5 w-2.5 text-white" />
          </span>
        )}
      </div>
      <span className="text-[11px] font-semibold text-slate-700 truncate w-full text-center flex items-center justify-center gap-0.5">
        {bundle.is_mine ? "Vous" : (bundle.author.name || "—").replace(/^Dr\s+/, "")}
        {bundle.author.blue_check && <VerifiedBadge size={9} />}
      </span>
    </button>
  );
}

function StoryViewer({ bundles, startIdx, onClose, onChange, refresh }) {
  const [bIdx, setBIdx] = useState(startIdx);
  const [sIdx, setSIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const intervalRef = useRef(null);
  const viewedSetRef = useRef(new Set());
  const DURATION = 5000; // 5s per story

  const bundle = bundles[bIdx];
  const story = bundle?.stories?.[sIdx];

  // Reset progress and mark as viewed when story changes
  useEffect(() => {
    setProgress(0);
    if (story && !story.is_mine && !story.viewed && !viewedSetRef.current.has(story.story_id)) {
      viewedSetRef.current.add(story.story_id);
      api.post(`/stories/${story.story_id}/view`).catch(() => {});
    }
  // eslint-disable-next-line
  }, [bIdx, sIdx]);

  useEffect(() => {
    if (paused || !story) return;
    const step = 50;
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        const np = p + (step / DURATION) * 100;
        if (np >= 100) {
          clearInterval(intervalRef.current);
          next();
          return 100;
        }
        return np;
      });
    }, step);
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line
  }, [bIdx, sIdx, paused]);

  const next = () => {
    if (!bundle) return;
    if (sIdx + 1 < bundle.stories.length) {
      setSIdx(sIdx + 1);
    } else if (bIdx + 1 < bundles.length) {
      setBIdx(bIdx + 1); setSIdx(0); onChange?.(bIdx + 1);
    } else {
      onClose(); refresh?.();
    }
  };

  const prev = () => {
    if (sIdx > 0) setSIdx(sIdx - 1);
    else if (bIdx > 0) {
      const prevBundle = bundles[bIdx - 1];
      setBIdx(bIdx - 1); setSIdx((prevBundle?.stories?.length || 1) - 1); onChange?.(bIdx - 1);
    }
  };

  const deleteStory = async () => {
    if (!story?.is_mine) return;
    if (!confirm("Supprimer cette story ?")) return;
    try {
      await api.delete(`/stories/${story.story_id}`);
      toast.success("Story supprimée");
      onClose(); refresh?.();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const loadViewers = async () => {
    if (!story?.is_mine) return;
    try {
      const { data } = await api.get(`/stories/${story.story_id}/viewers`);
      setViewers(data); setShowViewers(true); setPaused(true);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  if (!bundle || !story) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="story-viewer">
      <div className="relative w-full max-w-md h-full sm:h-[90vh] sm:rounded-2xl overflow-hidden flex flex-col" style={{ background: story.image_url ? "#000" : story.background }} onClick={(e) => e.stopPropagation()}>
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
          {bundle.stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
              <div className="h-full bg-white transition-all" style={{ width: i < sIdx ? "100%" : i === sIdx ? `${progress}%` : "0%" }} />
            </div>
          ))}
        </div>
        {/* Header */}
        <div className="relative z-20 pt-5 px-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center font-bold border border-white/40">
            {(bundle.author.name || "?").replace(/^Dr\s+/, "").charAt(0)}
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="font-bold text-sm flex items-center gap-1">{bundle.author.name}{bundle.author.blue_check && <VerifiedBadge size={11} />}</div>
            <div className="text-[11px] opacity-80">{timeLeft(story.expires_at)}</div>
          </div>
          {story.is_mine && (
            <button onClick={deleteStory} className="text-white/80 hover:text-white p-1" data-testid="story-delete-btn"><Trash2 className="h-4 w-4" /></button>
          )}
          <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="story-close-btn"><X className="h-5 w-5" /></button>
        </div>

        {/* Tap zones */}
        <button className="absolute left-0 top-12 bottom-16 w-1/3 z-10" onClick={prev} aria-label="prev"><span className="sr-only">prev</span></button>
        <button className="absolute right-0 top-12 bottom-16 w-1/3 z-10" onClick={next} aria-label="next"><span className="sr-only">next</span></button>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center px-8 py-12 text-center text-white relative">
          {story.image_url && <img src={story.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="relative z-10 max-w-sm">
            <div className="text-2xl sm:text-3xl font-display font-extrabold leading-tight drop-shadow-lg whitespace-pre-wrap">{story.content}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-20 px-4 pb-5 text-white">
          {story.is_mine ? (
            <button onClick={loadViewers} className="text-xs flex items-center gap-1 opacity-90 hover:opacity-100" data-testid="story-viewers-btn">
              <Eye className="h-3.5 w-3.5" />{story.viewers_count} vue{story.viewers_count > 1 ? "s" : ""}
            </button>
          ) : (
            <div className="text-[11px] opacity-70">Story partagée · Doctobook</div>
          )}
        </div>

        {/* Viewers list */}
        {showViewers && (
          <div className="absolute inset-0 z-30 bg-black/85 backdrop-blur p-5 overflow-y-auto" onClick={() => { setShowViewers(false); setPaused(false); }}>
            <div className="text-white text-sm font-bold mb-3">Vues ({viewers.length})</div>
            <div className="space-y-2">
              {viewers.length === 0 ? <div className="text-white/60 text-xs">Personne n'a encore vu cette story.</div> :
                viewers.map((v) => (
                  <div key={v.user_id} className="flex items-center gap-2 text-white">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">{(v.name || "?").charAt(0)}</div>
                    <div className="text-sm">{v.name}{v.blue_check && <VerifiedBadge size={10} />}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateStoryDialog({ open, onOpenChange, onCreated }) {
  const [content, setContent] = useState("");
  const [bg, setBg] = useState(BG_PALETTE[0]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/stories", { content, background: bg });
      toast.success("Story publiée pour 24 h");
      setContent(""); setBg(BG_PALETTE[0]);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle className="sr-only">Créer une story 24 h</DialogTitle>
        <DialogDescription className="sr-only">Publier un message éphémère visible 24 heures par votre réseau.</DialogDescription>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FF7A5C] mb-1">Story 24 h</div>
          <h3 className="font-display text-xl font-extrabold text-deep">Partagez un message éphémère</h3>
          <p className="text-xs text-slate-500 mt-1">Visible 24 h par votre réseau. Astuces, cas cliniques, rappels santé…</p>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="rounded-2xl overflow-hidden h-56 flex items-center justify-center p-6 text-center text-white shadow-inner" style={{ background: bg }}>
            <div className="font-display text-xl font-extrabold whitespace-pre-wrap leading-tight">{content || "Tapez votre message…"}</div>
          </div>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="Votre message (280 caractères max)" maxLength={280} data-testid="story-content-input" />
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Fond</div>
            <div className="flex gap-2 flex-wrap">
              {BG_PALETTE.map((g) => (
                <button type="button" key={g} onClick={() => setBg(g)} className={`w-9 h-9 rounded-full border-2 transition ${bg === g ? "border-[#FF7A5C] scale-110" : "border-white"} shadow-sm`} style={{ background: g }} data-testid={`bg-${BG_PALETTE.indexOf(g)}`} />
              ))}
            </div>
          </div>
          <Button type="submit" disabled={submitting || !content.trim()} className="w-full btn-coral rounded-full" data-testid="story-publish-btn">
            {submitting ? "Publication…" : "Publier ma story (24 h)"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function StoriesBar() {
  const { user } = useAuth();
  const isPro = ["doctor", "pharmacy"].includes(user?.role);
  const [bundles, setBundles] = useState([]);
  const [viewerIdx, setViewerIdx] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/stories");
      setBundles(data);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { load(); }, []);

  if (bundles.length === 0 && !isPro) return null;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3 mb-6 shadow-sm overflow-x-auto">
        <div className="flex items-center gap-3">
          {isPro && <StoryCircle isCreate user={user} onClick={() => setOpenCreate(true)} />}
          {bundles.map((b, i) => (
            <StoryCircle key={b.author.user_id} bundle={b} onClick={() => setViewerIdx(i)} />
          ))}
          {bundles.length === 0 && isPro && (
            <div className="text-xs text-slate-400 ms-2">Soyez le premier à publier une story 24 h.</div>
          )}
        </div>
      </div>

      {viewerIdx !== null && (
        <StoryViewer
          bundles={bundles}
          startIdx={viewerIdx}
          onClose={() => setViewerIdx(null)}
          onChange={setViewerIdx}
          refresh={load}
        />
      )}

      <CreateStoryDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
    </>
  );
}
